import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { StatusBadge } from '../../../StatusBadge';
import { Typography } from '../../../Typography';
import { CELL_PAD_X, DATE_SAMPLES, EDGE_PAD, HEADER_FLOOR_CAP, HEADER_LABEL_GAP, SORT_GLYPH_WIDTH, SWITCH_WIDTH, TRAILING_CELL_PAD_START } from '../table.constants';
import styles from '../table.module.scss';
const samplesOf = (column)=>{
    var _column_format;
    var _column_width;
    if (column.type === 'date') return [
        DATE_SAMPLES[(_column_format = column.format) !== null && _column_format !== void 0 ? _column_format : 'date']
    ];
    const floorSample = (_column_width = column.width) === null || _column_width === void 0 ? void 0 : _column_width.floorSample;
    if (floorSample == null) return [];
    // WHY: candidates key the probe spans, and two locale labels may coincide.
    return typeof floorSample === 'string' ? [
        floorSample
    ] : Array.from(new Set(floorSample));
};
const startPadOf = (index, isTrailing)=>{
    if (index === 0) return EDGE_PAD;
    return isTrailing ? TRAILING_CELL_PAD_START : CELL_PAD_X;
};
const probesFor = (columns, trailingId)=>columns.map((column, index)=>({
            id: String(column.name),
            label: column.header,
            sortable: column.sortable === true,
            samples: samplesOf(column),
            sampleKind: column.type === 'badge' ? 'badge' : 'text',
            fixedContent: column.type === 'toggle' ? SWITCH_WIDTH : undefined,
            startPad: startPadOf(index, String(column.name) === trailingId)
        }));
const sameFloors = (a, b)=>{
    const keys = Object.keys(a);
    return keys.length === Object.keys(b).length && keys.every((key)=>a[key] === b[key]);
};
// WHY: probes depend on labels and types only, so measuring cannot feed back into the widths.
export function useColumnFloors(param) {
    let { mainColumn, columns, trailingColumn, headerLabelVariant = 'paragraph3', sampleTestId } = param;
    const probes = useMemo(()=>probesFor([
            ...mainColumn == null ? [] : [
                mainColumn
            ],
            ...columns,
            ...trailingColumn == null ? [] : [
                trailingColumn
            ]
        ], trailingColumn == null ? undefined : String(trailingColumn.name)), [
        mainColumn,
        columns,
        trailingColumn
    ]);
    const hostRef = useRef(null);
    const [floors, setFloors] = useState({});
    useLayoutEffect(()=>{
        var _document_fonts;
        const host = hostRef.current;
        if (host == null) return undefined;
        const measure = ()=>{
            const next = {};
            probes.forEach((probe)=>{
                var _probe_fixedContent;
                const label = host.querySelector('[data-floor-label="'.concat(probe.id, '"]'));
                const samples = host.querySelectorAll('[data-floor-sample="'.concat(probe.id, '"]'));
                const labelWidth = label == null ? 0 : label.scrollWidth;
                // WHY: the widest candidate wins; which one that is depends on the font and the locale.
                const sampleWidth = (_probe_fixedContent = probe.fixedContent) !== null && _probe_fixedContent !== void 0 ? _probe_fixedContent : Array.from(samples).reduce((widest, el)=>Math.max(widest, el.scrollWidth), 0);
                if (labelWidth === 0 && sampleWidth === 0) return;
                const cellChrome = probe.startPad + CELL_PAD_X;
                const headerChrome = cellChrome + (probe.sortable ? SORT_GLYPH_WIDTH + HEADER_LABEL_GAP : 0);
                const headerFloor = labelWidth === 0 ? 0 : Math.min(labelWidth + headerChrome, HEADER_FLOOR_CAP);
                const sampleFloor = sampleWidth === 0 ? 0 : sampleWidth + cellChrome;
                next[probe.id] = Math.ceil(Math.max(headerFloor, sampleFloor));
            });
            setFloors((current)=>sameFloors(current, next) ? current : next);
        };
        measure();
        // WHY: the first paint can run on a fallback font.
        let cancelled = false;
        (_document_fonts = document.fonts) === null || _document_fonts === void 0 ? void 0 : _document_fonts.ready.then(()=>!cancelled && measure());
        return ()=>{
            cancelled = true;
        };
    }, [
        probes
    ]);
    const measurer = /*#__PURE__*/ _jsx("div", {
        ref: hostRef,
        "aria-hidden": true,
        className: styles.floorMeasurer,
        children: probes.map((probe)=>/*#__PURE__*/ _jsxs(React.Fragment, {
                children: [
                    /*#__PURE__*/ _jsx(Typography, {
                        as: "span",
                        variant: headerLabelVariant,
                        "data-floor-label": probe.id,
                        children: probe.label
                    }),
                    probe.samples.map((sample)=>probe.sampleKind === 'badge' ? /*#__PURE__*/ _jsx(StatusBadge, {
                            "data-floor-sample": probe.id,
                            testId: sampleTestId === null || sampleTestId === void 0 ? void 0 : sampleTestId(probe.id),
                            children: sample
                        }, sample) : /*#__PURE__*/ _jsx(Typography, {
                            as: "span",
                            "data-floor-sample": probe.id,
                            testId: sampleTestId === null || sampleTestId === void 0 ? void 0 : sampleTestId(probe.id),
                            children: sample
                        }, sample))
                ]
            }, probe.id))
    });
    return {
        floors,
        measurer
    };
}
